/**
 * exportUtils.js
 * Utility functions for exporting visualizations and tables.
 * Supports PNG (via html2canvas) and CSV export.
 * Role-gated: only superadmin and stakeholder can export.
 */

import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';

// ─── Role Check ───────────────────────────────────────────────────────────────

const EXPORT_ALLOWED_ROLES = ['superadmin', 'stakeholder'];

/**
 * Check if the given role is allowed to export data.
 * @param {string} role - User role
 * @returns {boolean}
 */
export function canExport(role) {
  return EXPORT_ALLOWED_ROLES.includes(role);
}

// ─── PNG Export ───────────────────────────────────────────────────────────────

/**
 * Capture a DOM element as a high-resolution PNG and trigger download.
 * Uses html-to-image which leverages the browser's native rendering.
 * @param {HTMLElement} element - The DOM element to capture
 * @param {string} filename - Desired filename (without extension)
 * @param {Object} [options] - Optional overrides
 * @returns {Promise<boolean>} - true on success, false on failure
 */
export async function exportElementAsPNG(element, filename, options = {}) {
  if (!element) {
    console.error('exportElementAsPNG: No element provided');
    return false;
  }

  try {
    // Hide export buttons and action buttons during capture
    const exportButtons = element.querySelectorAll('[data-export-btn]');
    const hideFromExport = element.querySelectorAll('[data-hide-on-export]');
    exportButtons.forEach(btn => { btn.style.display = 'none'; });
    hideFromExport.forEach(el => { el.style.display = 'none'; });

    // Temporarily apply export-specific styles (e.g., padding for margins)
    const tempStyles = options.style || {};
    const originalStyles = {};
    Object.keys(tempStyles).forEach(key => {
      originalStyles[key] = element.style[key];
      element.style[key] = tempStyles[key];
    });

    // Create a temporary wrapper that includes the element + footer
    // This avoids overflow:hidden clipping issues
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position: absolute; left: -9999px; top: 0; background: #ffffff; padding: 0;';
    wrapper.setAttribute('data-export-wrapper', 'true');

    // Clone the element into the wrapper
    const clone = element.cloneNode(true);
    clone.style.overflow = 'visible';
    clone.style.maxHeight = 'none';
    clone.style.position = 'relative';
    clone.style.width = element.offsetWidth + 'px';

    // Remove export buttons and hide-on-export elements from clone
    clone.querySelectorAll('[data-export-btn]').forEach(btn => btn.remove());
    clone.querySelectorAll('[data-hide-on-export]').forEach(el => el.remove());

    // Remove maxHeight/opacity on collapsible sections in clone
    clone.querySelectorAll('[style*="maxHeight"]').forEach(el => {
      el.style.maxHeight = 'none';
      el.style.opacity = '1';
      el.style.overflow = 'visible';
    });

    // Remove overflow-hidden classes from clone to prevent clipping
    clone.querySelectorAll('.overflow-hidden, .overflow-x-auto, .overflow-x-hidden').forEach(el => {
      el.classList.remove('overflow-hidden', 'overflow-x-auto', 'overflow-x-hidden');
      el.style.overflow = 'visible';
    });

    // Append footer to clone
    const footer = document.createElement('div');
    footer.style.cssText = 'margin-top: 16px; padding: 12px 16px 4px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8;';
    footer.innerHTML = `
      <div style="display: flex; gap: 24px; align-items: center;">
        <span>Data on file updated on: <strong style="color: #64748b;">17-04-2026</strong></span>
        <span>Patient Profile updated on: <strong style="color: #64748b;">15-06-2026</strong></span>
      </div>
      <div style="margin-top: 4px; font-size: 8px; color: #a1a1aa;">© 2026 Immunoadoptive Cell Therapy Private Limited. All rights reserved.</div>
    `;
    clone.appendChild(footer);

    // Add bottom padding to ensure footer is not clipped
    clone.style.paddingBottom = '28px';

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    const { style, ...toPngOptions } = options;

    const dataUrl = await toPng(clone, {
      pixelRatio: 2,
      backgroundColor: '#ffffff',
      cacheBust: true,
      ...toPngOptions,
    });

    // Remove temporary wrapper
    document.body.removeChild(wrapper);

    // Restore temporary styles on original element
    Object.keys(originalStyles).forEach(key => {
      element.style[key] = originalStyles[key];
    });

    // Restore hidden elements
    exportButtons.forEach(btn => { btn.style.display = ''; });
    hideFromExport.forEach(el => { el.style.display = ''; });

    // Convert data URL to blob and download
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    const sanitizedName = sanitizeFilename(filename);
    saveAs(blob, `${sanitizedName}.png`);

    return true;
  } catch (err) {
    console.error('exportElementAsPNG failed:', err);
    // Cleanup wrapper if still present
    const staleWrapper = document.querySelector('[data-export-wrapper]');
    if (staleWrapper) document.body.removeChild(staleWrapper);
    // Restore hidden elements even on failure
    const exportButtons = element.querySelectorAll('[data-export-btn]');
    const hideFromExport = element.querySelectorAll('[data-hide-on-export]');
    exportButtons.forEach(btn => { btn.style.display = ''; });
    hideFromExport.forEach(el => { el.style.display = ''; });
    return false;
  }
}

// ─── CSV Export ───────────────────────────────────────────────────────────────

/**
 * Generate a CSV file from headers and rows, and trigger download.
 * Handles CSV injection prevention and UTF-8 BOM for Excel compatibility.
 * @param {string[]} headers - Column headers
 * @param {Array<Array<string|number|null>>} rows - 2D array of row data
 * @param {string} filename - Desired filename (without extension)
 * @returns {boolean} - true on success
 */
export function exportTableAsCSV(headers, rows, filename) {
  if (!headers || !rows) {
    console.error('exportTableAsCSV: Missing headers or rows');
    return false;
  }

  try {
    const csvRows = [];

    // Header row
    csvRows.push(headers.map(h => escapeCSVCell(h)).join(','));

    // Data rows
    rows.forEach(row => {
      csvRows.push(row.map(cell => escapeCSVCell(cell)).join(','));
    });

    // UTF-8 BOM for Excel compatibility
    const BOM = '\uFEFF';
    const csvContent = BOM + csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const sanitizedName = sanitizeFilename(filename);
    saveAs(blob, `${sanitizedName}.csv`);

    return true;
  } catch (err) {
    console.error('exportTableAsCSV failed:', err);
    return false;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Escape a cell value for CSV.
 * Prevents CSV injection by prefixing dangerous characters.
 * @param {*} value
 * @returns {string}
 */
function escapeCSVCell(value) {
  if (value === null || value === undefined) return '';

  let str = String(value);

  // CSV injection prevention: prefix dangerous leading characters
  if (/^[=+\-@\t\r]/.test(str)) {
    str = `'${str}`;
  }

  // If cell contains comma, quotes, or newlines — wrap in quotes
  if (/[,"\n\r]/.test(str)) {
    str = `"${str.replace(/"/g, '""')}"`;
  }

  return str;
}

/**
 * Sanitize filename to prevent path traversal or invalid characters.
 * @param {string} name
 * @returns {string}
 */
function sanitizeFilename(name) {
  return name
    .replace(/[/\\?%*:|"<>]/g, '-')
    .replace(/\.\./g, '')
    .replace(/\s+/g, '_')
    .slice(0, 200); // Reasonable max length
}

/**
 * Generate a timestamped filename with context.
 * @param {string} prefix - e.g., 'KM_Curve', 'Risk_Table'
 * @param {Object} [context] - Optional context { indication, analysisType, filter }
 * @returns {string}
 */
export function generateExportFilename(prefix, context = {}) {
  const parts = [prefix];

  if (context.indication) parts.push(context.indication);
  if (context.analysisType) parts.push(context.analysisType);
  if (context.filter) parts.push(context.filter);

  const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  parts.push(date);

  return parts.join('_');
}
